import { EntryStatusBadge, RunStatusBadge } from './-runStatus';
import { useReportOnce } from './-useReportOnce';
import billingApi from '@api/billingApi';
import DefaultPage from '@components/defaultPage';
import { LoadingIndicator } from '@components/loadingIndicator';
import { RequireClaim } from '@components/requireClaim';
import useAuth from '@hooks/useAuth';
import { useTranslator } from '@i18n';
import { Anchor, Badge, Group, Text } from '@mantine/core';
import type { BillingRunDetails } from '@models/billingRun';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { Table, textFilter, type Column } from 'schummar-table';

export const Route = createFileRoute('/profile/billing/runs/$id')({
  component: () => (
    <RequireClaim claim="orgs/billing" scope="global" fallback={<Navigate to="/profile" replace />}>
      <RunPage />
    </RequireClaim>
  ),
});

type Row = BillingRunDetails['entries'][number];

/** The invoice is joined as it is now, so its amounts and Odoo invoice are not this run's any more */
const replacedSince = ({ entry, invoice }: Row) => entry.moveId != null && invoice?.moveId !== entry.moveId;

/** Estimate-only rows and replaced invoices have no pricing of their own to show */
const priced = (row: Row) => (row.entry.accountReference && !replacedSince(row) ? row.invoice : null);

function RunPage() {
  const t = useTranslator();
  const { id } = Route.useParams();
  const { loaded, hasClaim } = useAuth();
  const [details, error] = billingApi.run(id).useCache({
    disabled: !loaded || !hasClaim('orgs/billing', 'global'),
    updateOnMount: true,
  });
  useReportOnce(error, !!details);

  const amount = (value?: number | null, currency?: string | null) =>
    value == null
      ? '-'
      : t.numberFormat(
          value,
          currency ? { style: 'currency', currency } : { minimumFractionDigits: 2, maximumFractionDigits: 2 },
        );

  const columns: Column<Row, any>[] = [
    {
      id: 'name',
      header: t('billingRuns.organization'),
      value: ({ entry }) => entry.name,
      width: 'minmax(20ch, 2fr)',
      filter: textFilter(),
    },
    {
      id: 'status',
      header: t('billingRuns.statusLabel'),
      value: ({ entry }) => entry.status,
      renderCell: (status) => <EntryStatusBadge status={status} />,
      width: 'max-content',
    },
    {
      id: 'deviceCountBilled',
      header: t('billing.columns.deviceCountBilled'),
      value: ({ entry }) => entry.deviceCountBilled,
      width: 'max-content',
      classes: { cell: 'justify-end', headerCell: 'justify-end' },
    },
    {
      id: 'amountUntaxed',
      header: t('billingRuns.amountUntaxed'),
      // A run that did not bill shows what it would have cost
      value: (row) => (replacedSince(row) ? null : (row.invoice?.amountUntaxed ?? row.entry.estimate ?? null)),
      renderCell: (value, { entry, invoice }) =>
        value != null && invoice?.amountUntaxed == null && entry.estimate != null ? (
          <Text size="sm" c="dimmed" title={t('billingRuns.estimate')}>
            ~{amount(value, entry.currency)}
          </Text>
        ) : (
          amount(value, invoice?.currency)
        ),
      width: 'max-content',
      displaySize: 'desktop',
    },
    {
      id: 'amountTotal',
      header: t('billingRuns.amountTotal'),
      value: (row) => (replacedSince(row) ? null : (row.invoice?.amountTotal ?? null)),
      renderCell: (value, { invoice }) => amount(value, invoice?.currency),
      width: 'max-content',
      displaySize: 'desktop',
    },
    {
      id: 'pricelist',
      header: t('billingRuns.pricelist'),
      value: (row) => {
        const invoice = priced(row);
        return invoice ? (invoice.pricelistName ?? t('billingRuns.listPrice')) : '';
      },
      width: 'max-content',
      displaySize: 'desktop',
    },
    {
      id: 'priceUnit',
      header: t('billingRuns.priceUnit'),
      value: (row) => priced(row)?.priceUnit ?? null,
      renderCell: (value, { invoice }) => amount(value, invoice?.currency),
      width: 'max-content',
      displaySize: 'desktop',
    },
    {
      id: 'invoice',
      header: t('billingRuns.invoice'),
      value: ({ entry, invoice }) => invoice?.moveName ?? invoice?.odooRef ?? entry.invoiceId ?? '',
      renderCell: (name, row) => (
        <div>
          {replacedSince(row) ? (
            <Text size="sm" c="dimmed" data-testid="billing-run-replaced-since">
              {t('billingRuns.replacedSince')}
            </Text>
          ) : row.odooUrl ? (
            <Anchor href={row.odooUrl} target="_blank" rel="noreferrer" data-testid="billing-run-odoo-link">
              {name || t('billingRuns.openInOdoo')}
            </Anchor>
          ) : (
            name
          )}
          {row.invoice?.replacements
            .filter((replacement) => replacement.runId === id)
            .map((replacement) => (
              <Text key={replacement.moveId} size="xs" c="dimmed" data-testid="billing-run-replaced">
                {t('billingRuns.replaces', {
                  // Odoo names drafts "/"
                  invoice:
                    replacement.moveName && replacement.moveName !== '/'
                      ? replacement.moveName
                      : `#${replacement.moveId}`,
                })}
              </Text>
            ))}
        </div>
      ),
      width: 'minmax(max-content, 1fr)',
    },
    {
      id: 'error',
      header: t('billingRuns.error'),
      value: ({ entry }) => entry.error ?? '',
      renderCell: (error) => (
        <Text size="sm" c="red" css={{ whiteSpace: 'pre-wrap' }}>
          {error}
        </Text>
      ),
      width: 'minmax(20ch, 2fr)',
    },
  ];

  return (
    <DefaultPage
      title={details ? `${t('billingRuns.run')} ${details.run.month}` : t('billingRuns.run')}
      backButton={{ to: '/profile/billing/runs' }}
      css={{ display: 'flex', flexDirection: 'column', gap: 'var(--mantine-spacing-md)', paddingBottom: 50 }}
    >
      {!details ? (
        error ? null : (
          <LoadingIndicator />
        )
      ) : (
        <>
          <Group gap="sm">
            <RunStatusBadge status={details.run.status} />
            {details.run.force && (
              <Badge color="orange" variant="outline">
                {t('billingRuns.forced')}
              </Badge>
            )}
            <Text size="sm" c="dimmed">
              {t.dateTimeFormat(new Date(details.run.startedAt))}
              {details.run.finishedAt && ` - ${t.dateTimeFormat(new Date(details.run.finishedAt))}`}
            </Text>
          </Group>

          {details.run.error && (
            <Text c="red" css={{ whiteSpace: 'pre-wrap' }}>
              {details.run.error}
            </Text>
          )}

          {details.entries.length === 0 ? (
            <Text c="dimmed">{t('billingRuns.noEntries')}</Text>
          ) : (
            <Table
              items={details.entries}
              id={(row) => row.entry.orgId}
              columns={columns}
              fullWidth
              enableSelection={false}
              enableColumnSelection={false}
              enableExport={false}
              defaultSort={[{ columnId: 'name', direction: 'asc' }]}
              displaySize={{ mobile: 700, desktop: Infinity }}
              stickyHeader={{ top: 'var(--app-shell-header-height)' }}
              wrapRow={(props) => <div {...props} data-testid="billing-run-entry" />}
              rowDetails={({ entry }) =>
                entry.locations.length > 0 || entry.deviceCountNotInstalled > 0 ? (
                  <div data-testid="billing-run-locations">
                    {entry.locations.map((location) => (
                      <Text key={location.locationId} size="sm">
                        {location.name}: {location.deviceCountBilled}
                      </Text>
                    ))}
                    {entry.deviceCountNotInstalled > 0 && (
                      <Text size="sm">
                        {t('billing.notInstalled')}: {entry.deviceCountNotInstalled}
                      </Text>
                    )}
                  </div>
                ) : undefined
              }
            />
          )}
        </>
      )}
    </DefaultPage>
  );
}
